/**
 * BpmnInsertEdge - Linha do mapa com botão "+" para inserir um bloco no meio
 *
 * Desenha a ligação em ângulo reto (vertical) e, no ponto médio, um botão "+"
 * que abre a lista de blocos. Ao escolher, o construtor religa a linha original
 * para o novo bloco e cria a saída dele para o destino anterior. Apresentação
 * apenas — toda persistência acontece nas RPCs chamadas pelo construtor.
 *
 * @version 1.1.0
 * RELEASE: 2026-08-31 v1.1.0 - Botão "+" abre no primeiro clique (o evento não
 *          é mais consumido pela seleção da linha).
 * RELEASE: 2026-08-31 v1.0.0 - Criação inicial (inserção guiada de blocos)
 */

import { BaseEdge, EdgeLabelRenderer, getSmoothStepPath, type EdgeProps } from '@xyflow/react';
import { Plus } from 'lucide-react';
import { NodePickerPopover } from './NodePickerPopover';
import type { WorkflowPaletteItem } from '../workflowNodeMeta';

export interface BpmnInsertEdgeData extends Record<string, unknown> {
  rotulo?: string | null;
  regressao?: boolean;
  podeEditar?: boolean;
  onInserir?: (edgeId: string, item: WorkflowPaletteItem) => void;
}

export function BpmnInsertEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  markerEnd,
  selected,
  data,
}: EdgeProps) {
  const info = (data ?? {}) as BpmnInsertEdgeData;

  const [path, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    borderRadius: 12,
  });

  return (
    <>
      <BaseEdge
        id={id}
        path={path}
        markerEnd={markerEnd}
        style={{
          stroke: selected
            ? 'hsl(var(--primary))'
            : info.regressao
              ? 'hsl(var(--destructive))'
              : 'hsl(var(--muted-foreground))',
          strokeWidth: selected ? 2 : 1.5,
          strokeDasharray: info.regressao ? '6 4' : undefined,
        }}
      />

      <EdgeLabelRenderer>
        <div
          className="nodrag nopan pointer-events-auto absolute flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-1"
          style={{ transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)` }}
        >
          {info.rotulo && (
            <span className="rounded-full border bg-card px-2 py-0.5 text-[10px] font-medium text-foreground shadow-sm">
              {info.rotulo}
            </span>
          )}

          {info.podeEditar && (
            <NodePickerPopover
              onSelect={(item) => info.onInserir?.(id, item)}
              trigger={
                <button
                  type="button"
                  title="Inserir bloco nesta linha"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  className="flex h-6 w-6 items-center justify-center rounded-full border border-primary/40 bg-card text-primary shadow-sm transition-colors hover:bg-primary hover:text-primary-foreground"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              }
            />
          )}
        </div>
      </EdgeLabelRenderer>
    </>
  );
}
